/**
 * Imports the necessary dependencies for the ResetPasswordContainer component.
 * @module ResetPasswordContainer
 * @requires React
 * @requires useEffect
 * @requires useState
 * @requires Constant
 * @requires connect
 * @requires useNavigate
 * @requires ResetPasswordComponent
 */
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Constant from "../../../util/constant";

import { useTranslation } from "react-i18next";
import { setOrganiserPasswordAction } from "../../../store/features/organiser/organiserSlice";
import { showToast } from "../../../store/features/toast/toastSlice";
import SetPasswordComponent from "./setPasswordComponent";

const SetPasswordContainer = (props) => {
  /**
   * Initializes the necessary state variables for the navigation, visibility, and busy status.
   * Also defines an array of error fields for a multi-step form validation.
   * @param {function} useNavigate - The hook for navigating to different routes.
   * @returns None
   */
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [show, setShow] = useState(true);
  const [showconfirm, setShowConfirm] = useState(true);
  const busy = false;
  let { token } = useParams();

  const multiErrorFields = [
    { length: t("MINIMUM_8_CHARACTERS") },
    { hasSpecialChar: t("MINIMUM_1_SPECIAL_CHARACTER") },
    { hasNumbers: t("MINIMUM_1_NUMERIC_CHARACTER") },
    { hasUppercase: t("MINIMUM_1_UPPERCASE_CHARACTER") },
    { hasLowerCase: t("MINIMUM_1_LOWWERCASE_CHARACTER") },
  ];

  /**
   * Toggles the value of the "show" state variable.
   * @returns None
   */
  const t_show = () => {
    setShow((prevShow) => !prevShow);
  };

  /**
   * Toggles the value of `showConfirm` state variable.
   * @returns None
   */
  const t_show2 = () => {
    setShowConfirm((prevShowConfirm) => !prevShowConfirm);
  };

  /**
   * A reset form object that defines the structure and validation rules for the form fields.
   * @property {{name: string, validate: object}} newpassword - The new password field, with validation rules.
   * @property {{name: string}} password - The password field.
   */
  const resetForm = {
    newpassword: {
      name: "newpassword",
      validate: {
        required: {
          value: true,
          message: t("PLEASE_ENTER_A_PASSWORD"),
        },
        validate: {
          hasUppercase: (value) =>
            value && value.match(Constant.REGEX.UPPERCASE) !== null,
          hasLowerCase: (value) =>
            value && value.match(Constant.REGEX.LOWERCASE) !== null,
          hasNumbers: (value) =>
            value && value.match(Constant.REGEX.NUMBER) !== null,
          hasSpecialChar: (value) =>
            value && value.match(Constant.REGEX.SPECIALCHARACTERS) !== null,
          length: (value) => (value && value.length >= 8) || "",
        },
      },
    },
    password: {
      name: "password",
    },
  };

  /**
   * Handles the submission of a form.
   * @param {{any}} value - The value submitted from the form.
   * @returns None
   */
  const onFormSubmit = async (value) => {
    const data = {
      token: token,
      password: value.password,
    };
    dispatch(setOrganiserPasswordAction(data)).then((resultAction) => {
      if (setOrganiserPasswordAction.fulfilled.match(resultAction)) {
        dispatch(
          showToast({
            show: false,
            message: resultAction?.payload?.message,
            type: "success",
          })
        );
        navigate("/");
      } else if (setOrganiserPasswordAction.rejected.match(resultAction)) {
        dispatch(
          showToast({
            show: false,
            message: resultAction?.error?.message,
            type: "error",
          })
        );
      }
    });
  };
  const onClickCancel = async (value) => {
    navigate("/");
  };

  return (
    <SetPasswordComponent
      onFormSubmit={onFormSubmit}
      resetForm={resetForm}
      t_show2={t_show2}
      t_show={t_show}
      onClickCancel={onClickCancel}
      multiErrorFields={multiErrorFields}
      busy={busy}
      showconfirm={showconfirm}
      show={show}
    />
  );
};

export default SetPasswordContainer;
